import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Bell, Send, Users, User, Loader2, CheckCircle, Search } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Visitor {
  id: string;
  full_name: string;
  email: string;
  user_id: string | null;
}

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  
  const [users, setUsers] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Form state
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [message, setMessage] = useState("");
  const [messageAr, setMessageAr] = useState("");
  const [notificationType, setNotificationType] = useState("announcement");
  const [recipientType, setRecipientType] = useState<"all" | "selected">("all");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("visitors")
      .select("id, full_name, email, user_id")
      .not("user_id", "is", null)
      .order("full_name");

    if (error) {
      console.error("Error fetching users:", error);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.user_id!));
    }
  };

  const handleSendNotifications = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error(isRTL ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill all required fields");
      return;
    }

    if (recipientType === "selected" && selectedUsers.length === 0) {
      toast.error(isRTL ? "يرجى اختيار مستخدم واحد على الأقل" : "Please select at least one user");
      return;
    }

    setSending(true);

    try {
      const targetUserIds = recipientType === "all" 
        ? users.map((u) => u.user_id!).filter(Boolean)
        : selectedUsers;

      const notifications = targetUserIds.map((userId) => ({
        user_id: userId,
        title: title,
        title_ar: titleAr || null,
        message: message,
        message_ar: messageAr || null,
        type: notificationType,
        is_read: false,
      }));

      const { error } = await supabase
        .from("notifications")
        .insert(notifications);

      if (error) throw error;

      toast.success(
        isRTL 
          ? `تم إرسال الإشعار إلى ${targetUserIds.length} مستخدم` 
          : `Notification sent to ${targetUserIds.length} users`
      );

      // Reset form
      setTitle("");
      setTitleAr("");
      setMessage("");
      setMessageAr("");
      setSelectedUsers([]);
      setRecipientType("all");
    } catch (error) {
      console.error("Error sending notifications:", error);
      toast.error(isRTL ? "حدث خطأ أثناء إرسال الإشعارات" : "Error sending notifications");
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Bell className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-white">
            {isRTL ? "إرسال إشعارات" : "Send Notifications"}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Notification Form */}
          <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Send className="w-5 h-5" />
                {isRTL ? "تفاصيل الإشعار" : "Notification Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Notification Type */}
              <div className="space-y-2">
                <Label className="text-white/70">{isRTL ? "نوع الإشعار" : "Notification Type"}</Label>
                <Select value={notificationType} onValueChange={setNotificationType}>
                  <SelectTrigger className="bg-white/10 border-white/15 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="announcement">{isRTL ? "إعلان" : "Announcement"}</SelectItem>
                    <SelectItem value="event">{isRTL ? "فعالية" : "Event"}</SelectItem>
                    <SelectItem value="reminder">{isRTL ? "تذكير" : "Reminder"}</SelectItem>
                    <SelectItem value="update">{isRTL ? "تحديث" : "Update"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label className="text-white/70">{isRTL ? "العنوان (إنجليزي) *" : "Title (English) *"}</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={isRTL ? "عنوان الإشعار بالإنجليزية" : "Notification title in English"} className="bg-white/10 border-white/15 text-white placeholder:text-white/40" />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">{isRTL ? "العنوان (عربي)" : "Title (Arabic)"}</Label>
                <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} placeholder={isRTL ? "عنوان الإشعار بالعربية" : "Notification title in Arabic"} dir="rtl" className="bg-white/10 border-white/15 text-white placeholder:text-white/40" />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label className="text-white/70">{isRTL ? "الرسالة (إنجليزي) *" : "Message (English) *"}</Label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={isRTL ? "محتوى الإشعار بالإنجليزية" : "Notification content in English"} rows={4} className="bg-white/10 border-white/15 text-white placeholder:text-white/40" />
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">{isRTL ? "الرسالة (عربي)" : "Message (Arabic)"}</Label>
                <Textarea value={messageAr} onChange={(e) => setMessageAr(e.target.value)} placeholder={isRTL ? "محتوى الإشعار بالعربية" : "Notification content in Arabic"} rows={4} dir="rtl" className="bg-white/10 border-white/15 text-white placeholder:text-white/40" />
              </div>

              {/* Send Button */}
              <Button onClick={handleSendNotifications} disabled={sending || !title.trim() || !message.trim()} className="w-full" size="lg">
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {isRTL ? "جاري الإرسال..." : "Sending..."}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {isRTL 
                      ? `إرسال إلى ${recipientType === "all" ? users.length : selectedUsers.length} مستخدم`
                      : `Send to ${recipientType === "all" ? users.length : selectedUsers.length} users`}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Recipients Selection */}
          <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5" />
                {isRTL ? "اختيار المستلمين" : "Select Recipients"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Recipient Type */}
              <RadioGroup value={recipientType} onValueChange={(value) => setRecipientType(value as "all" | "selected")} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all" className="cursor-pointer text-white/70">
                    {isRTL ? "جميع المستخدمين" : "All Users"} ({users.length})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="selected" id="selected" />
                  <Label htmlFor="selected" className="cursor-pointer text-white/70">
                    {isRTL ? "مستخدمين محددين" : "Selected Users"}
                  </Label>
                </div>
              </RadioGroup>

              {recipientType === "selected" && (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4`} />
                    <Input
                      type="search"
                      placeholder={isRTL ? "البحث عن مستخدم..." : "Search users..."}
                      className={`${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} bg-white/10 border-white/15 text-white placeholder:text-white/40`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Select All */}
                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="text-sm font-medium text-white/70">
                        {isRTL ? "تحديد الكل" : "Select All"}
                      </span>
                    </div>
                    <span className="text-sm text-white/50">
                      {selectedUsers.length} {isRTL ? "محدد" : "selected"}
                    </span>
                  </div>

                  {/* Users List */}
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="max-h-[400px] overflow-y-auto space-y-2">
                      {filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-white/50">
                          <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>{isRTL ? "لا يوجد مستخدمين" : "No users found"}</p>
                        </div>
                      ) : (
                        filteredUsers.map((user) => (
                          <div
                            key={user.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedUsers.includes(user.user_id!)
                                ? "bg-primary/10 border-primary/30"
                                : "border-white/10 hover:bg-white/5"
                            }`}
                            onClick={() => user.user_id && handleUserToggle(user.user_id)}
                          >
                            <Checkbox
                              checked={selectedUsers.includes(user.user_id!)}
                              onCheckedChange={() => user.user_id && handleUserToggle(user.user_id)}
                            />
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate text-white">{user.full_name}</p>
                              <p className="text-sm text-white/50 truncate">{user.email}</p>
                            </div>
                            {selectedUsers.includes(user.user_id!) && (
                              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              {recipientType === "all" && (
                <div className="text-center py-8 bg-white/5 rounded-lg">
                  <Users className="w-16 h-16 mx-auto mb-3 text-primary opacity-50" />
                  <p className="text-white/60">
                    {isRTL 
                      ? `سيتم إرسال الإشعار لجميع المستخدمين (${users.length})`
                      : `Notification will be sent to all users (${users.length})`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </AdminPageBackground>
  );
};

export default Notifications;
